/* assets/baby-poster-renderer.js
   Baby Poster renderer:
   - Preview: canvas (fast)
   - Export: JPG preview + PDF @ 300 DPI, exact mm size
   - Layers:
       1) Full poster background (WHITE)
       2) Colored RECT (artboard) at the top
       3) Baby inside colored rect (clamped, NEVER squished)
       4) Name + Meta on the WHITE area UNDER the rect

   ✅ modelCm-only scaling:
   Assumption: image naturalHeight corresponds to modelCm (full height == baby length).
   If assets have different padding/crop, this will vary between models.

   ✅ UPDATED FOR SHOPIFY BLOCKS:
   - Keys are dynamic (block.id)
   - Models are created/updated at runtime via setBabyModelAssetUrl + setBabyModelCalibration
   - cmToPosterFactor may come in as string (e.g. "0.82" or "0,82")
*/

(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.BabyPosterRenderer = factory();
})(typeof self !== "undefined" ? self : this, function () {
  // "Design space" (logical coordinates)
  const DESIGN_W = 900;
  const DESIGN_H = 1200;

  // Print sizes in mm
  const SIZE_MM = {
    "30x40": { w: 300, h: 400 },
    "40x50": { w: 400, h: 500 },
    "50x70": { w: 500, h: 700 },
  };

  // With blocks, keys are dynamic. We'll keep a dynamic registry.
  let DEFAULT_BABY_KEY = null;

  // Defaults used when a model isn't fully configured from the section
  const DEFAULT_MODEL_CM = 57;
  const DEFAULT_CM_TO_POSTER_FACTOR = 0.82;
  const DEFAULT_DIRECTION = "left";

  // Dynamic map: key -> model
  const BABY_MODELS = {};

  const DEFAULT_STYLE = {
    // Colors
    rectColor: "#EDEBE7",
    posterBgColor: "#FFFFFF",
    textColor: "#111111",
    fontFamily: "RockwellPoster",

    // Outer margins for rect
    marginTop: 66,
    marginLeft: 66,
    marginRight: 66,

    // White footer under rect for text
    footerHeight: 114,
    footerGap: 22,
    footerPaddingX: 70,
    footerPaddingBottom: 30,

    // Text styling
    nameSize: 13,
    metaSize: 9,
    letterSpacingName: 8,
    letterSpacingMeta: 7,

    // Text positions inside footer (measured from footer bottom)
    NAME_Y_FROM_FOOTER_BOTTOM: 68,
    META_Y_FROM_FOOTER_BOTTOM: 45,

    // Baby shadow
    babyShadow: true,

    // Debug
    debug: true,
  };

  // -------------------------
  // Helpers
  // -------------------------
  function getArtboardRect(style) {
    const x = style.marginLeft;
    const y = style.marginTop;
    const w = DESIGN_W - style.marginLeft - style.marginRight;
    const h = DESIGN_H - style.marginTop - style.footerGap - style.footerHeight;
    return { x, y, w, h: Math.max(1, h) };
  }

  function getFooterRect(style, artboard) {
    const x = 0;
    const y = artboard.y + artboard.h + style.footerGap;
    const w = DESIGN_W;
    const h = Math.max(1, DESIGN_H - y);
    return { x, y, w, h };
  }

  function safeFilename(s) {
    return String(s || "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9\-_]/g, "");
  }

  function downloadDataUrl(dataUrl, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = dataUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function mmToPx(mm, dpi) {
    return Math.round((mm / 25.4) * dpi);
  }

  function makeOffscreenCanvas(pxW, pxH) {
    const off = document.createElement("canvas");
    off.width = pxW;
    off.height = pxH;
    const offCtx = off.getContext("2d");

    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";

    // map DESIGN coords -> pixels (can be non-uniform to fit print size)
    offCtx.setTransform(pxW / DESIGN_W, 0, 0, pxH / DESIGN_H, 0, 0);

    return { off, offCtx };
  }

  // ✅ Preview canvas setup (ratio per sizeKey)
  function setupPreviewCanvas(canvas, ctx, dprMax = 3, sizeKey) {
    const dpr = Math.max(1, Math.min(dprMax, window.devicePixelRatio || 1));
    const rect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));

    let ratio = DESIGN_H / DESIGN_W; // fallback
    if (sizeKey) {
      const mm = SIZE_MM[sizeKey] || SIZE_MM["30x40"];
      ratio = (Number(mm.h) || 400) / (Number(mm.w) || 300);
    }

    const cssH = Math.max(1, Math.floor(cssW * ratio));
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);

    ctx.setTransform(
      canvas.width / DESIGN_W,
      0,
      0,
      canvas.height / DESIGN_H,
      0,
      0,
    );

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
  }

  function setupPreviewCanvasForSize(canvas, ctx, sizeKey, dprMax = 3) {
    return setupPreviewCanvas(canvas, ctx, dprMax, sizeKey);
  }

  async function ensureFontLoaded(style = DEFAULT_STYLE) {
    if (!document.fonts || !document.fonts.load) return;
    await document.fonts.load(
      `400 ${style.nameSize}px ${style.fontFamily}`,
      "ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ",
    );
    await document.fonts.load(
      `400 ${style.metaSize}px ${style.fontFamily}`,
      "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ",
    );
    if (document.fonts.ready) await document.fonts.ready;
  }

  async function loadImageTo(imgEl, url) {
    return new Promise((resolve, reject) => {
      imgEl.onload = async () => {
        try {
          if (imgEl.decode) await imgEl.decode();
        } catch (_) {}
        resolve();
      };
      imgEl.onerror = reject;
      imgEl.src = url;
    });
  }

  function drawSpacedText(ctx, text, centerX, y, letterSpacing) {
    const chars = String(text || "").split("");
    let totalWidth = 0;
    for (const ch of chars)
      totalWidth += ctx.measureText(ch).width + letterSpacing;
    totalWidth = Math.max(0, totalWidth - letterSpacing);

    let x = centerX - totalWidth / 2;
    for (const ch of chars) {
      ctx.fillText(ch, x, y);
      x += ctx.measureText(ch).width + letterSpacing;
    }
  }

  function fitTextSize(ctx, text, maxWidth, startPx, minPx, fontFamily) {
    let px = startPx;
    while (px >= minPx) {
      ctx.font = `${px}px ${fontFamily}, RockwellPoster`;
      if (ctx.measureText(text).width <= maxWidth) return px;
      px -= 0.5;
    }
    return minPx;
  }

  // -------------------------
  // Model parsing / registry
  // -------------------------
  function parseFactor(val, fallback = DEFAULT_CM_TO_POSTER_FACTOR) {
    // supports number, "0.82", "0,82"
    if (val == null) return fallback;
    if (typeof val === "number" && isFinite(val)) return val;

    const s = String(val).trim();
    if (!s) return fallback;

    const n = parseFloat(s.replace(",", "."));
    return isFinite(n) && n > 0 ? n : fallback;
  }

  function parseModelCm(val, fallback = DEFAULT_MODEL_CM) {
    const n = Number(val);
    return isFinite(n) && n > 0 ? n : fallback;
  }

  function ensureModel(key) {
    let k = key || DEFAULT_BABY_KEY;
    if (!k) k = "__default__";

    if (!BABY_MODELS[k]) {
      BABY_MODELS[k] = {
        key: k,
        label: k,
        assetUrl: "",
        modelCm: DEFAULT_MODEL_CM,
        cmToPosterFactor: DEFAULT_CM_TO_POSTER_FACTOR,
        direction: DEFAULT_CM_TO_POSTER_FACTOR,
      };
    }

    if (!DEFAULT_BABY_KEY || DEFAULT_BABY_KEY === "__default__") {
      DEFAULT_BABY_KEY = k;
    }

    return BABY_MODELS[k];
  }

  function setBabyModelAssetUrl(key, url) {
    const m = ensureModel(key);
    m.assetUrl = url || "";
  }

  function setBabyModelCalibration(
    key,
    { modelCm, cmToPosterFactor, direction } = {},
  ) {
    const m = ensureModel(key);
    m.modelCm = parseModelCm(modelCm, m.modelCm);

    // NOTE: cmToPosterFactor may be string
    m.cmToPosterFactor = parseFactor(cmToPosterFactor, m.cmToPosterFactor);
    m.direction = direction;
  }

  function getBabyModel(key) {
    if (key && BABY_MODELS[key]) return BABY_MODELS[key];
    if (DEFAULT_BABY_KEY && BABY_MODELS[DEFAULT_BABY_KEY])
      return BABY_MODELS[DEFAULT_BABY_KEY];
    return ensureModel(key);
  }

  function upsertBabyModel(
    key,
    { assetUrl, modelCm, cmToPosterFactor, startInverted, direction },
  ) {
    BABY_MODELS[key] = {
      assetUrl: assetUrl,
      modelCm: modelCm,
      direction: direction,
      startInverted: startInverted,
      cmToPosterFactor: cmToPosterFactor, // string OK
    };
  }

  function setDefaultBabyKey(key) {
    DEFAULT_BABY_KEY = key;
  }

  // -------------------------
  // Core math helpers
  // -------------------------
  function getPaperHeightMm(sizeKey) {
    const mm = SIZE_MM[sizeKey] || SIZE_MM["30x40"];
    return Number(mm.h) || 400;
  }

  function getPosterPxPerCm(ctx, sizeKey) {
    const tr = ctx.getTransform();
    const sy = tr.d || 1; // DESIGN->px scale in Y
    const posterPxH = DESIGN_H * sy; // full poster height in device pixels
    const paperHeightCm = getPaperHeightMm(sizeKey) / 10;
    return paperHeightCm > 0 ? posterPxH / paperHeightCm : 0;
  }

  // -------------------------
  // Baby render (NEVER squish)
  // + flipX support (mirror)
  // -------------------------
  function drawBabyInsideArtboard(ctx, babyImg, input, style, artboard) {
    if (!babyImg || !(babyImg.naturalWidth || babyImg.width)) return;

    const model = getBabyModel(input && input.babyType);

    // Transform scales (may be non-uniform)
    const tr = ctx.getTransform();
    const sx = tr.a || 1;
    const sy = tr.d || 1;

    // Source image px
    const iw = babyImg.naturalWidth || babyImg.width;
    const ih = babyImg.naturalHeight || babyImg.height;

    // Input cm (user)
    const inputCm = isFinite(Number(input.lengthCm))
      ? Number(input.lengthCm)
      : 0;

    // Per-model factor (cm -> poster cm)  (can be string)
    const factor = parseFactor(model.cmToPosterFactor, 1);
    const targetPosterCm = inputCm * factor;

    // Selected paper size
    const sizeKey = input.sizeKey || "30x40";

    // Poster px per cm (preview/export consistent)
    const pxPerCm = getPosterPxPerCm(ctx, sizeKey);
    const targetPosterPxLen = targetPosterCm * pxPerCm;

    // Model cm (the “reference baby length” for the asset)
    const modelCm = parseModelCm(model.modelCm, inputCm || 1);

    // Source px per cm (ASSUMPTION: image height == modelCm)
    const sourcePxPerCm = ih / modelCm;

    // Target scale in pixel space
    const targetScalePx = ih > 0 ? targetPosterPxLen / ih : 1;

    let drawPxW = iw * targetScalePx;
    let drawPxH = ih * targetScalePx;

    // Clamp to artboard (physical constraint)
    const artPxW = artboard.w * sx;
    const artPxH = artboard.h * sy;

    let clamped = false;
    if (drawPxH > artPxH) {
      const f = artPxH / drawPxH;
      drawPxW *= f;
      drawPxH *= f;
      clamped = true;
    }
    if (drawPxW > artPxW) {
      const f = artPxW / drawPxW;
      drawPxW *= f;
      drawPxH *= f;
      clamped = true;
    }

    // Center in pixel-space
    const artPxX = artboard.x * sx;
    const artPxY = artboard.y * sy;

    const xPx = artPxX + (artPxW - drawPxW) / 2;
    const yPx = artPxY + (artPxH - drawPxH) / 2;

    // Convert back to DESIGN-space for drawImage
    const x = xPx / sx;
    const y = yPx / sy;
    const drawW = drawPxW / sx;
    const drawH = drawPxH / sy;

    let flipX = !!(input && input.direction !== model.direction);
    console.log(flipX, model.startInverted, input.direction, model.direction);

    if (style.debug) {
      const achievedPosterCm = pxPerCm > 0 ? drawPxH / pxPerCm : 0;
      console.log("[Baby render debug - dynamic models + string factor]", {
        sizeKey,
        babyType: input && input.babyType,
        inputCm: Number(inputCm.toFixed(2)),
        modelCm,
        cmToPosterFactorRaw: model.cmToPosterFactor,
        cmToPosterFactor: factor,
        targetPosterCm: Number(targetPosterCm.toFixed(2)),
        pxPerCm: Number(pxPerCm.toFixed(2)),
        sourcePxPerCm: Number(sourcePxPerCm.toFixed(2)),
        targetPosterPxLen: Math.round(targetPosterPxLen),
        achievedPosterCm: Number(achievedPosterCm.toFixed(2)),
        clamped,
        flipX,
        imgPx: { w: iw, h: ih },
        drawPx: { w: Math.round(drawPxW), h: Math.round(drawPxH) },
      });
    }

    // Draw (mirror only the baby around its center)
    const cx = x + drawW / 2;
    const cy = y + drawH / 2;

    ctx.save();

    if (style.babyShadow) {
      ctx.shadowColor = "rgba(0,0,0,0.12)";
      ctx.shadowBlur = 16;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
    }

    ctx.translate(cx, cy);
    if (flipX) ctx.scale(-1, 1);
    ctx.drawImage(babyImg, -drawW / 2, -drawH / 2, drawW, drawH);

    ctx.restore();
  }

  // -------------------------
  // Poster render
  // -------------------------
  function renderPoster(ctx, babyImg, input, styleOverride) {
    const style = { ...DEFAULT_STYLE, ...(styleOverride || {}) };

    const artboard = getArtboardRect(style);
    const footer = getFooterRect(style, artboard);

    // 1) Clear + WHITE full background
    ctx.clearRect(0, 0, DESIGN_W, DESIGN_H);
    ctx.fillStyle = style.posterBgColor || "#FFFFFF";
    ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);

    // 2) Colored rect (artboard)
    ctx.fillStyle = style.rectColor || "#EDEBE7";
    ctx.fillRect(artboard.x, artboard.y, artboard.w, artboard.h);

    // 3) Baby inside rect
    drawBabyInsideArtboard(ctx, babyImg, input, style, artboard);

    // 4) Text on WHITE footer
    const centerX = DESIGN_W / 2;
    const maxWidth = DESIGN_W - style.footerPaddingX * 2;

    ctx.fillStyle = style.textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const footerBottomY = footer.y + footer.h - style.footerPaddingBottom;

    // Name
    const name = String(input.name || "")
      .trim()
      .toUpperCase();
    const nameY = footerBottomY - style.NAME_Y_FROM_FOOTER_BOTTOM;

    const namePx = fitTextSize(
      ctx,
      name,
      maxWidth,
      style.nameSize,
      10,
      style.fontFamily,
    );
    ctx.font = `400 ${namePx}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, name, centerX, nameY, style.letterSpacingName);

    console.log(input);

    const weightPart = (input.weightDisplay || "").trim();
    const lengthPart = (input.lengthDisplay || "").trim();
    // Meta
    const meta = [
      weightPart,
      lengthPart,
      input.dateStr ? String(input.dateStr).trim() : null,
    ]
      .filter(Boolean)
      .join("  |  ");

    const metaY = footerBottomY - style.META_Y_FROM_FOOTER_BOTTOM;
    ctx.font = `400 ${style.metaSize}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, meta, centerX, metaY, style.letterSpacingMeta);
  }

  // -------------------------
  // Exports
  // -------------------------
  function getPreviewPixels({ longSide = 1200 } = {}) {
    const ratio = DESIGN_H / DESIGN_W; // jpg preview helper (legacy)
    const w = longSide;
    const h = Math.round(longSide * ratio);
    return { w, h };
  }

  async function exportJpg({
    input,
    babyImg,
    style,
    longSide = 1200,
    quality = 0.9,
    sizeKey,
  }) {
    let w, h;
    if (sizeKey && SIZE_MM[sizeKey]) {
      const mm = SIZE_MM[sizeKey];
      const ratio = mm.h / mm.w;
      h = longSide;
      w = Math.round(longSide / ratio);
    } else {
      const px = getPreviewPixels({ longSide });
      w = px.w;
      h = px.h;
    }
    const inputWithSize = { ...(input || {}), sizeKey: sizeKey || (input && input.sizeKey) };
    const { off, offCtx } = makeOffscreenCanvas(w, h);
    renderPoster(offCtx, babyImg, inputWithSize, style);
    return off.toDataURL("image/jpeg", quality);
  }

  async function exportPdf({
    input,
    babyImg,
    style,
    size,
    dpi = 300,
    jpegQuality = 0.95,
  }) {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      throw new Error(
        "jsPDF not loaded. Add the jsPDF script tag on this page.",
      );
    }

    const mm = SIZE_MM[size];
    if (!mm) throw new Error("Unknown size for PDF: " + size);

    const pxW = mmToPx(mm.w, dpi);
    const pxH = mmToPx(mm.h, dpi);

    const { off, offCtx } = makeOffscreenCanvas(pxW, pxH);

    // Ensure sizeKey exists for physical cm math
    const inputWithSize = { ...(input || {}), sizeKey: size };

    renderPoster(offCtx, babyImg, inputWithSize, style);

    const imgData = off.toDataURL("image/jpeg", jpegQuality);

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
      orientation: mm.h >= mm.w ? "portrait" : "landscape",
      unit: "mm",
      format: [mm.w, mm.h],
    });

    pdf.addImage(imgData, "JPEG", 0, 0, mm.w, mm.h);
    return pdf;
  }

  function dataUrlToBlob(dataUrl) {
    const parts = dataUrl.split(",");
    const mime = parts[0].match(/:(.*?);/)[1];
    const bin = atob(parts[1]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  function calcPrintLongSide(sizeKey, dpi) {
    dpi = dpi || 300;
    const mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    var longMm = Math.max(mm.w, mm.h);
    return Math.round((longMm / 25.4) * dpi);
  }

  return {
    DESIGN_W,
    DESIGN_H,
    SIZE_MM,
    DEFAULT_STYLE,

    setupPreviewCanvas,
    setupPreviewCanvasForSize,
    ensureFontLoaded,

    loadImageTo,
    renderPoster,
    upsertBabyModel,
    setDefaultBabyKey,

    exportJpg,
    exportPdf,

    downloadDataUrl,
    safeFilename,

    // dynamic model API
    setBabyModelAssetUrl,
    getBabyModel,
    setBabyModelCalibration,

    // print helpers
    dataUrlToBlob,
    calcPrintLongSide,
  };
});
